// ============================================================================
// JARVISTRAVEL - TOAST
// Transient, on-brand feedback for recoverable errors + confirmations — the
// standard replacement for window.alert(). The store lives in ./toastStore so
// ANY code can fire one (React components, the API layer, the global error
// handler) via the `toast` API; this file is only the renderer.
//
// THE STATE IS NOT HERE ON PURPOSE. `react-refresh/only-export-components`
// forbids a non-component export from a component file, and web-app lints with
// `--max-warnings 0`. Moving `toast` or a test seam back into this file breaks
// the required Quality Gate (JAR-1328 review). Import from ./toastStore.
// ============================================================================

import { useSyncExternalStore } from 'react';
import { CheckCircle2, AlertCircle, Info, X, type LucideIcon } from 'lucide-react';
import { subscribe, getSnapshot, dismiss, type ToastType } from './toastStore';

const META: Record<ToastType, { Icon: LucideIcon; tone: string }> = {
  success: { Icon: CheckCircle2, tone: 'text-success' },
  error: { Icon: AlertCircle, tone: 'text-danger' },
  info: { Icon: Info, tone: 'text-accent' },
};

/** The render region for toasts. Mount once near the app root. */
export function Toaster() {
  const list = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  if (list.length === 0) return null;
  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[70] flex flex-col items-center gap-2 pl-[calc(1rem+env(safe-area-inset-left))] pr-[calc(1rem+env(safe-area-inset-right))] pb-[calc(1rem+env(safe-area-inset-bottom))] pointer-events-none"
      role="region"
      aria-label="Notifications"
    >
      {list.map((t) => {
        const { Icon, tone } = META[t.type];
        return (
          <div
            key={t.id}
            role="status"
            aria-live={t.type === 'error' ? 'assertive' : 'polite'}
            className="pointer-events-auto w-full max-w-sm flex items-start gap-2.5 bg-surface border border-border rounded-xl shadow-lg px-3.5 py-3 animate-reveal motion-reduce:animate-none"
          >
            <Icon className={`w-5 h-5 shrink-0 mt-px ${tone}`} strokeWidth={2} aria-hidden="true" />
            <p className="flex-1 min-w-0 text-sm text-body">{t.message}</p>
            {t.action && (
              <button
                type="button"
                onClick={() => { dismiss(t.id); t.action?.onClick(); }}
                className="shrink-0 text-sm font-semibold text-accent hover:underline"
              >
                {t.action.label}
              </button>
            )}
            <button type="button" onClick={() => dismiss(t.id)} aria-label="Dismiss notification" className="shrink-0 -mr-1 p-1.5 text-muted hover:text-ink rounded-full transition-colors tap-44">
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default Toaster;
